// Adapted from lora-scan packages/face-qc/src/guidance.ts (SHOT_PLAN, faceExpected): the FaceLock Scan checklist, owner plan 2026-09-30.
/**
 * The FaceLock Scan checklist: which pictures a complete set holds.
 *
 * Three slow-turn clips (face at arm's length, upper body waist-up, full body
 * head to feet) and 17 buckets across four groups. A scan fills every bucket
 * to its `count` (24 frames, always). Photos uploaded when there is no camera
 * fill the same buckets: at least one each, 16 to 24 in all (what the server
 * accepts).
 *
 * `faceExpected` keeps lora-scan's meaning: whether a frontal face detector is
 * expected to read these frames. False for true profiles and for full-body
 * sides and backs; a miss there is normal and never a mistake.
 * `eyesVisible` says whether the open-eyes check applies.
 *
 * Array order is capture order: the order a slow turn passes through them.
 */

export const SCAN_CLIPS = Object.freeze(["face", "upper", "full"]);

export const SCAN_GROUPS = Object.freeze(["face", "expression", "upper", "full"]);

const b = (id, clip, group, count, faceExpected, eyesVisible) => Object.freeze({ id, clip, group, count, faceExpected, eyesVisible });

export const SCAN_BUCKETS = Object.freeze([
  b("face_front", "face", "face", 2, true, true),
  b("face_left", "face", "face", 2, true, true),
  b("face_profile_left", "face", "face", 1, false, false),
  b("face_right", "face", "face", 2, true, true),
  b("face_profile_right", "face", "face", 1, false, false),
  b("face_chin_up", "face", "face", 1, true, true),
  b("face_chin_down", "face", "face", 1, true, true),
  b("expr_neutral", "face", "expression", 1, true, true),
  b("expr_smile", "face", "expression", 2, true, true),
  b("expr_talk", "face", "expression", 2, true, true),
  b("upper_front", "upper", "upper", 2, true, true),
  b("upper_left", "upper", "upper", 1, true, true),
  b("upper_right", "upper", "upper", 1, true, true),
  // lora-scan's full_body_front expects a face (the server's detector reads it
  // at that size); sides and back do not.
  b("full_front", "full", "full", 2, true, false),
  b("full_left", "full", "full", 1, false, false),
  b("full_back", "full", "full", 1, false, false),
  b("full_right", "full", "full", 1, false, false),
]);

/** A full scan: every bucket to its count. */
export const SCAN_TOTAL = SCAN_BUCKETS.reduce((n, x) => n + x.count, 0);
/** Uploaded photos: what the server accepts. */
export const UPLOAD_MIN_TOTAL = 16;
export const UPLOAD_MAX_TOTAL = SCAN_TOTAL;

export function bucketById(id) {
  return SCAN_BUCKETS.find((x) => x.id === id) || null;
}

export function bucketsOfClip(clip) {
  return SCAN_BUCKETS.filter((x) => x.clip === clip);
}

export function bucketsOfGroup(group) {
  return SCAN_BUCKETS.filter((x) => x.group === group);
}

/**
 * A scan's own plan: how many pictures it holds per bucket. The base plan is
 * the checklist above (24). A plan may ask for more of a bucket (at most
 * PLAN_MAX_EXTRA_PER_BUCKET over its count), never fewer: base views cannot be
 * dropped. What is over the base count is "extra" (extraOf), derived, never
 * stored. Uploaded photos never follow a plan.
 */
export const PLAN_VERSION = 1;
export const PLAN_MAX_EXTRA_PER_BUCKET = 4;

const BASE_PLAN = Object.freeze({ v: PLAN_VERSION, counts: Object.freeze(Object.fromEntries(SCAN_BUCKETS.map((x) => [x.id, x.count]))) });

/** Every bucket to its count: today's 24. Frozen; the same object every call. */
export function basePlan() {
  return BASE_PLAN;
}

/**
 * A valid plan from anything (a saved copy, a hand-built one): unknown bucket
 * ids are dropped, a missing or short count is raised to the bucket's count,
 * a count is capped at that plus PLAN_MAX_EXTRA_PER_BUCKET. Not a plan of this
 * version: the base plan.
 */
export function cleanPlan(raw) {
  if (raw === BASE_PLAN) return BASE_PLAN;
  if (!raw || typeof raw !== "object" || raw.v !== PLAN_VERSION || !raw.counts || typeof raw.counts !== "object") return BASE_PLAN;
  if (raw.rescan === true) return cleanRescan(raw);
  let extra = 0;
  const counts = {};
  for (const x of SCAN_BUCKETS) {
    const n = Math.floor(Number(raw.counts[x.id]));
    counts[x.id] = Number.isFinite(n) ? Math.max(x.count, Math.min(x.count + PLAN_MAX_EXTRA_PER_BUCKET, n)) : x.count;
    extra += counts[x.id] - x.count;
  }
  return extra === 0 ? BASE_PLAN : Object.freeze({ v: PLAN_VERSION, counts: Object.freeze(counts) });
}

/**
 * A RESCAN plan (an avatar enhancement's FaceShot SharpShoot): the scan again
 * of the parts the person picks, in another moment. Every bucket of a picked
 * part to its count, every other bucket 0, so the flow walks only those parts
 * (openBuckets, coverage and nextOpenClip all follow the plan). Unknown parts
 * are ignored; no part at all is the face part.
 */
export function rescanPlan(parts) {
  const want = new Set((Array.isArray(parts) ? parts : []).filter((p) => SCAN_CLIPS.includes(p)));
  if (!want.size) want.add("face");
  return Object.freeze({ v: PLAN_VERSION, rescan: true,
    counts: Object.freeze(Object.fromEntries(SCAN_BUCKETS.map((x) => [x.id, want.has(x.clip) ? x.count : 0]))) });
}

/** A rescan plan cleaned: a bucket is 0 or its base count (to base + PLAN_MAX_EXTRA_PER_BUCKET); none at all is the face part. */
function cleanRescan(raw) {
  const counts = {};
  let any = 0;
  for (const x of SCAN_BUCKETS) {
    const n = Math.floor(Number(raw.counts[x.id]));
    counts[x.id] = Number.isFinite(n) && n > 0 ? Math.max(x.count, Math.min(x.count + PLAN_MAX_EXTRA_PER_BUCKET, n)) : 0;
    any += counts[x.id];
  }
  return any ? Object.freeze({ v: PLAN_VERSION, rescan: true, counts: Object.freeze(counts) }) : rescanPlan(["face"]);
}

/** The parts (clips) a plan asks for pictures of. */
export function planParts(plan) {
  const counts = cleanPlan(plan).counts;
  return SCAN_CLIPS.filter((c) => SCAN_BUCKETS.some((x) => x.clip === c && counts[x.id] > 0));
}

/** Pictures a scan on this plan holds. */
export function planTotal(plan) {
  return Object.values(cleanPlan(plan).counts).reduce((n, c) => n + c, 0);
}

/** Pictures of a bucket over its base count. */
export function extraOf(plan, id) {
  const x = bucketById(id);
  return x ? cleanPlan(plan).counts[id] - x.count : 0;
}

/**
 * How far a set is. `filled` maps bucket id -> frames held.
 * mode "scan": every bucket of the plan to its plan count (the base plan: 24),
 * one row per bucket the plan asks for, in checklist order. mode "upload": at
 * least one in every bucket, UPLOAD_MIN_TOTAL..UPLOAD_MAX_TOTAL in all (a plan
 * never changes the photo path).
 */
export function coverage(filled, mode = "scan", plan = BASE_PLAN) {
  const own = cleanPlan(plan);
  const counts = own.counts;
  const rows = mode === "upload"
    ? SCAN_BUCKETS.map((x) => {
      const got = Math.max(0, Math.min(x.count, Number(filled[x.id]) || 0));
      return { id: x.id, group: x.group, clip: x.clip, want: x.count, need: 1, got, done: got >= 1 };
    })
    : SCAN_BUCKETS.filter((x) => counts[x.id] > 0).map((x) => {
      const want = counts[x.id];
      const got = Math.max(0, Math.min(want, Number(filled[x.id]) || 0));
      return { id: x.id, group: x.group, clip: x.clip, want, need: want, got, done: got >= want };
    });
  const total = rows.reduce((n, r) => n + r.got, 0);
  const missing = rows.filter((r) => !r.done).map((r) => r.id);
  const complete = missing.length === 0 && (mode === "upload"
    ? total >= UPLOAD_MIN_TOTAL && total <= UPLOAD_MAX_TOTAL
    : total === planTotal(own));
  return { rows, total, missing, complete };
}
